import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useScanStore } from '@/stores/scanStore'
import type { WebSocketMessage } from '@/types/api'
import type { ScanProgress } from '@/types/scan'
import type { ScanResult } from '@/types/scan'

class WebSocketService {
  private socket: Socket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 1000 // Start with 1 second

  connect(): void {
    if (this.socket?.connected) {
      return
    }

    const { tokens } = useAuthStore.getState()
    if (!tokens?.access_token) {
      console.warn('No access token available for WebSocket connection')
      return
    }

    const { setConnectionStatus } = useUIStore.getState()
    setConnectionStatus('connecting')

    this.socket = io('/ws', {
      auth: {
        token: tokens.access_token,
      },
      transports: ['websocket'],
      timeout: 10000,
    })

    this.setupEventHandlers()
  }

  private setupEventHandlers(): void {
    if (!this.socket) return

    const { setConnectionStatus, addToast } = useUIStore.getState()
    const { setScanProgress, updateScan } = useScanStore.getState()

    this.socket.on('connect', () => {
      if (import.meta.env.DEV) console.log('WebSocket connected')
      setConnectionStatus('connected')
      this.reconnectAttempts = 0
      this.reconnectInterval = 1000

      addToast({
        title: 'Connected',
        description: 'Real-time updates enabled',
        type: 'success',
        duration: 3000,
      })
    })

    this.socket.on('disconnect', (reason) => {
      if (import.meta.env.DEV) console.log('WebSocket disconnected:', reason)
      setConnectionStatus('disconnected')

      // Don't show disconnect toast for intentional disconnections
      if (reason !== 'io client disconnect') {
        addToast({
          title: 'Connection Lost',
          description: 'Real-time updates temporarily unavailable',
          type: 'warning',
        })
      }

      this.handleReconnection()
    })

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error)
      setConnectionStatus('error')
      this.handleReconnection()
    })

    // Handle scan progress updates
    this.socket.on('scan_progress', (data: ScanProgress) => {
      if (import.meta.env.DEV) console.log('Scan progress update:', data)
      setScanProgress(data.scan_id, data)
    })

    // Handle scan completion
    this.socket.on('scan_completed', (data: ScanResult) => {
      if (import.meta.env.DEV) console.log('Scan completed:', data)
      updateScan(data.id, data)
      
      addToast({
        title: 'Scan Completed',
        description: `Security scan for ${data.repository_url} has finished`,
        type: 'success',
      })
    })

    // Handle scan failures
    this.socket.on('scan_failed', (data: { scan_id: string; error: string; scan?: ScanResult }) => {
      if (import.meta.env.DEV) console.log('Scan failed:', data)
      
      if (data.scan) {
        updateScan(data.scan_id, data.scan)
      }

      addToast({
        title: 'Scan Failed',
        description: data.error,
        type: 'error',
        duration: 10000,
      })
    })

    // Handle system messages
    this.socket.on('system_message', (data: { message: string; type: 'info' | 'warning' | 'error' }) => {
      addToast({
        title: 'System Notification',
        description: data.message,
        type: data.type,
        duration: data.type === 'error' ? 10000 : 5000,
      })
    })

    // Handle generic messages
    this.socket.on('message', (message: WebSocketMessage) => {
      if (import.meta.env.DEV) console.log('WebSocket message:', message)
      this.handleMessage(message)
    })
  }

  private handleMessage(message: WebSocketMessage): void {
    const { addToast } = useUIStore.getState()

    switch (message.type) {
      case 'scan_progress':
        if (message.data) {
          const { setScanProgress } = useScanStore.getState()
          setScanProgress((message.data as any).scan_id, message.data as ScanProgress)
        }
        break

      case 'scan_completed':
        if (message.data) {
          const { updateScan } = useScanStore.getState()
          const scanData = message.data as ScanResult
          updateScan(scanData.id, scanData)
        }
        break

      case 'scan_failed':
        if (message.data) {
          const { updateScan } = useScanStore.getState()
          const data = message.data as { scan_id: string; error: string; scan?: ScanResult }
          if (data.scan) {
            updateScan(data.scan_id, data.scan)
          }
        }
        break

      case 'system_message':
        if (message.data) {
          const data = message.data as { message: string; type: 'info' | 'warning' | 'error' }
          addToast({
            title: 'System Message',
            description: data.message,
            type: data.type,
          })
        }
        break

      default:
        if (import.meta.env.DEV) console.log('Unhandled WebSocket message type:', message.type)
    }
  }

  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      const { addToast } = useUIStore.getState()
      addToast({
        title: 'Connection Failed',
        description: 'Unable to establish real-time connection. Please refresh the page.',
        type: 'error',
        duration: 0, // Don't auto-dismiss
      })
      return
    }

    this.reconnectAttempts++

    setTimeout(() => {
      if (import.meta.env.DEV) console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`)
      this.connect()
    }, this.reconnectInterval)

    // Exponential backoff with jitter
    this.reconnectInterval = Math.min(this.reconnectInterval * 2 + Math.random() * 1000, 30000)
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    
    const { setConnectionStatus } = useUIStore.getState()
    setConnectionStatus('disconnected')
  }

  // Subscribe to scan updates for specific scan
  subscribeTo(scanId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { scan_id: scanId })
    }
  }

  // Unsubscribe from scan updates
  unsubscribeFrom(scanId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', { scan_id: scanId })
    }
  }

  // Check if connected
  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  // Send a message (for future features)
  send(event: string, data?: unknown): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    }
  }
}

export const websocketService = new WebSocketService()
export default websocketService